// useStandingTen — reads get_standing_ten(): the week's agent-curated 10 best legs
// (locked Mon–Sun) with results + weekly hit-rate/ROI. Honest-empty when no week exists.

import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface StandingLeg {
  rank: number;
  match_id: string;
  fixture: string;
  home_code: string | null;
  away_code: string | null;
  kickoff: string;
  market: string | null;
  selection: string;
  fair_odds: number | null;
  market_odds: number | null;
  clv_gap: number | null;
  conviction_tier: string | null;
  desk_confidence: number | null;
  rationale: string | null;
  result: "WON" | "LOST" | "VOID" | null;
  score: string | null;
}
export interface StandingStats {
  total: number; settled: number; won: number; lost: number; void: number; pending: number;
  hit_rate: number | null; roi: number | null;
}
export interface StandingTen {
  week_start: string | null;
  legs: StandingLeg[];
  stats: StandingStats;
}

export function useStandingTen(): { data: StandingTen | null; loading: boolean; error: string | null } {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [data, setData] = useState<StandingTen | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (supabase.rpc as unknown as (name: string) => Promise<{ data: unknown; error: { message: string } | null }>)("get_standing_ten")
      .then(({ data: d, error: e }) => {
        if (cancelled) return;
        if (e) { setError(e.message); setData(null); }
        else { setData((d ?? null) as StandingTen | null); }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supabase]);

  return { data, loading, error };
}
